/**
 * accessory-pickup.js
 * 配件选购功能：监听主产品 Add to Cart 按钮，收集选中的配件并批量加入购物车
 * 支持多变体下拉框切换
 */

/* 变体选择器 UI 更新 */
function updateAccessoryVariantUI(select) {
  const selectedOption = select.options[select.selectedIndex];
  if (!selectedOption) return;

  const itemRow = select.closest('.accessory-pickup__item');
  if (!itemRow) return;

  // Update product image
  const newImage = selectedOption.dataset.image;
  const productImage = itemRow.querySelector('[data-acc-image]');
  if (newImage && productImage) {
    productImage.src = newImage;
  }

  // Update price display
  const newPrice = selectedOption.dataset.price;
  const newCompareAtPrice = selectedOption.dataset.compareAtPrice;
  const priceContainer = itemRow.querySelector('.accessory-pickup__price');
  if (newPrice && priceContainer) {
    let priceHTML = '';
    if (newCompareAtPrice) {
      priceHTML = `
        <s class="accessory-pickup__original-price">${newCompareAtPrice}</s>
        <span class="accessory-pickup__sale-price">${newPrice}</span>`;
    } else {
      priceHTML = `<span class="accessory-pickup__sale-price">${newPrice}</span>`;
    }
    priceContainer.innerHTML = priceHTML;
  }

  // Update availability state of the checkbox
  const isAvailable = selectedOption.dataset.available === 'true';
  const checkbox = itemRow.querySelector('.accessory-pickup__checkbox');
  if (checkbox) {
    if (!isAvailable) {
      checkbox.disabled = true;
      checkbox.checked = false;
    } else {
      checkbox.disabled = false;
    }
  }
}

/* Web Component */
class AccessoryPickup extends HTMLElement {
  constructor() {
    super();
    this.variantSelects = this.querySelectorAll('.accessory-pickup-variant-select');
    if (this.variantSelects.length) {
      this.variantSelects.forEach((select) => {
        select.addEventListener('change', () => updateAccessoryVariantUI(select));
      });
    }

    // 点击整行选中/取消配件（排除变体下拉框区域）
    this.addEventListener('click', (event) => {
      const itemRow = event.target.closest('.accessory-pickup__item');
      if (!itemRow) return;

      // 排除变体下拉框内的点击
      if (event.target.closest('.accessory-pickup__variant-selector')) return;
      // 排除复选框本身（让其自然行为处理）
      if (event.target.closest('.accessory-pickup__checkbox')) return;

      const checkbox = itemRow.querySelector('.accessory-pickup__checkbox');
      if (checkbox && !checkbox.disabled) {
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        // 更新选中态样式
        this.updateSelectedState(itemRow, checkbox.checked);
      }
    });

    // 监听 checkbox change 事件，同步选中态样式
    this.addEventListener('change', (event) => {
      const checkbox = event.target;
      if (!checkbox.classList.contains('accessory-pickup__checkbox')) return;
      const itemRow = checkbox.closest('.accessory-pickup__item');
      if (itemRow) {
        this.updateSelectedState(itemRow, checkbox.checked);
      }
    });

    // 初始化选中态样式
    this.querySelectorAll('.accessory-pickup__checkbox:checked').forEach((cb) => {
      const itemRow = cb.closest('.accessory-pickup__item');
      if (itemRow) {
        this.updateSelectedState(itemRow, true);
      }
    });
  }

  updateSelectedState(itemRow, isSelected) {
    if (isSelected) {
      itemRow.classList.add('accessory-pickup__item--selected');
    } else {
      itemRow.classList.remove('accessory-pickup__item--selected');
    }
  }
}

if (!customElements.get('accessory-pickup')) {
  customElements.define('accessory-pickup', AccessoryPickup);
}

/* Fallback: 事件委托 */
document.addEventListener('change', function (event) {
  const select = event.target;
  if (!select.classList.contains('accessory-pickup-variant-select')) return;
  if (select.closest('accessory-pickup')) return;
  updateAccessoryVariantUI(select);
});

/**
 * v2: 拦截主产品 Add to Cart 提交
 * 收集所有选中的配件变体 ID，串行逐一加入购物车
 *
 * 架构改进:
 *   - 串行执行: 主产品 → 配件1 → 配件2 → ... → 统一刷新弹窗
 *   - 分步处理: 主产品失败 → 中断全部; 配件失败 → 跳过该配件继续
 *   - 最后统一渲染: 所有商品添加完毕后，用主产品响应渲染弹窗
 */
class AccessoryPickupCartHandler {
  constructor() {
    this.productForm = document.querySelector('product-form[data-section-id] form[data-type="add-to-cart-form"]');
    this.accessoryWrapper = document.querySelector('.accessory-pickup-wrapper');

    if (!this.productForm || !this.accessoryWrapper) return;

    this.init();
  }

  init() {
    const mainProductForm = document.querySelector('product-form');
    if (!mainProductForm) return;

    const form = mainProductForm.querySelector('form');
    if (!form) return;

    this.form = form;
    this.mainProductForm = mainProductForm;

    // capture 阶段 + stopImmediatePropagation 完全接管 submit
    form.addEventListener('submit', this.onSubmitHandler.bind(this), true);
  }

  getSelectedAccessories(mainQuantity = 1) {
    const items = [];
    const checkboxes = this.accessoryWrapper.querySelectorAll(
      '.accessory-pickup__checkbox:checked:not([disabled])'
    );

    checkboxes.forEach((checkbox) => {
      const itemRow = checkbox.closest('.accessory-pickup__item');
      if (!itemRow) return;

      const variantSelect = itemRow.querySelector('.accessory-pickup-variant-select');
      let variantId;

      if (variantSelect) {
        variantId = parseInt(variantSelect.value);
      } else {
        variantId = parseInt(itemRow.dataset.variantId);
      }

      if (variantId && !isNaN(variantId)) {
        items.push({ id: variantId, quantity: mainQuantity });
      }
    });

    return items;
  }

  /**
   * 增强版：兼容多种 Shopify 数量选择器格式
   */
  _getMainQuantity() {
    // 方式1: 标准 input[name="quantity"] — 先在 form 内找
    let input = this.form.querySelector('input[name="quantity"]');
    if (input && input.value) return Math.max(1, parseInt(input.value) || 1);

    // 方式2: select[name="quantity"] (部分主题使用)
    const select = this.form.querySelector('select[name="quantity"]');
    if (select && select.value) return Math.max(1, parseInt(select.value) || 1);

    // 方式3: Shopify 常用的 quantity__input 类名
    input = this.form.querySelector('.quantity__input, .quantity-input, [data-quantity-input]');
    if (input && input.value) return Math.max(1, parseInt(input.value) || 1);

    // 方式4: ★ 扩大到 product-form 和 product-info 容器查找（解决数量框在 form 外部的情况）
    const widerContainers = [
      this.form.closest('product-form'),
      this.form.closest('product-info'),
      document.querySelector('.product-form__quantity'),
      document.querySelector('[class*="quantity"][class*="product"]'),
    ];
    for (const container of widerContainers) {
      if (!container) continue;
      const wideInput = container.querySelector('input[name="quantity"]');
      if (wideInput && wideInput.value && wideInput !== input) {
        return Math.max(1, parseInt(wideInput.value) || 1);
      }
      const wideSelect = container.querySelector('select[name="quantity"]');
      if (wideSelect && wideSelect.value) {
        return Math.max(1, parseInt(wideSelect.value) || 1);
      }
    }

    console.warn('[Accessory Pickup] Could not find quantity input, defaulting to 1');
    return 1;
  }

  /**
   * 批量添加商品到购物车（一次请求，避免 429）
   * Shopify /cart/add.js 支持 items[] 数组格式：
   *   items[][id]=123&items[][quantity]=1&items[][id]=456&items[][quantity]=2
   * @param {Array<{id: number, quantity: number}>} items
   * @returns {{ ok: boolean, response?: object, errorMsg?: string }}
   */
  async addItemsToCart(items, sections, cart, retryCount = 0) {
    // 使用 URLSearchParams 构建 items[] 数组格式的 body
    const body = new URLSearchParams();
    items.forEach((item) => {
      body.append('items[][id]', item.id);
      body.append('items[][quantity]', item.quantity);
    });

    if (cart && sections) {
      body.append('sections', sections);
      body.append('sections_url', window.location.pathname);
    }

    let config;
    if (typeof fetchConfig === 'function') {
      config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    } else {
      config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      };
    }
    config.body = body;

    const res = await fetch(`${routes.cart_add_url}`, config);

    // 429 限流：指数退避重试，最多 2 次
    if (res.status === 429 && retryCount < 2) {
      const waitTime = 1000 * Math.pow(2, retryCount);
      console.warn(`[Accessory Pickup] Rate limited (429), retrying in ${waitTime}ms (${retryCount + 1}/2)...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      return this.addItemsToCart(items, sections, cart, retryCount + 1);
    }

    // 尝试解析 JSON
    let data;
    try {
      data = await res.json();
    } catch (e) {
      const text = await res.text().catch(() => '');
      const errorMsg = `Server returned ${res.status} (non-JSON): ${text.slice(0, 200)}`;
      console.error('[Accessory Pickup] JSON parse failed:', errorMsg);
      return { ok: false, errorMsg };
    }

    if (data.status) {
      const errorMsg = data.description || data.message || 'Error adding items';
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'accessory-pickup',
          errors: data.errors || errorMsg,
          message: errorMsg
        });
      }
      return { ok: false, errorMsg };
    }

    return { ok: true, response: data };
  }

  async onSubmitHandler(evt) {
    // 先读取主产品数量，以便配件同步使用
    const mainQuantity = this._getMainQuantity();

    const accessories = this.getSelectedAccessories(mainQuantity);

    // 没有配件: 让 product-form.js 正常接管
    if (accessories.length === 0) {
      return;
    }

    // 完全接管事件，阻止 product-form.js 的 handler 执行
    evt.preventDefault();
    evt.stopImmediatePropagation();

    const submitButton = this.mainProductForm.querySelector('[type="submit"]');
    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

    // 读取主产品 variant ID
    const mainVariantInput = this.form.querySelector('input[name="id"]');
    const mainVariantId = mainVariantInput ? parseInt(mainVariantInput.value) : null;

    if (!mainVariantId) return;

    // 获取 cart 组件
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    let sections = null;
    if (cart) {
      try {
        sections = cart.getSectionsToRender().map((s) => s.id);
      } catch (e) {
        console.warn('[Accessory Pickup] getSectionsToRender failed:', e);
      }
    }

    // ---- UI: 进入 loading 状态 ----
    this.handleErrorMessage();
    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }
    const loadingSpinner = this.mainProductForm.querySelector('.loading__spinner');
    if (loadingSpinner) loadingSpinner.classList.remove('hidden');

    let hasError = false;

    try {
      // ===== 构建批量 items 数组：主产品 + 所有选中配件 =====
      const allItems = [
        { id: mainVariantId, quantity: mainQuantity },
        ...accessories
      ];

      console.log('[Accessory Pickup] Batch adding', allItems.length, 'items:', allItems.map(i => `${i.id} x${i.quantity}`).join(', '));
      const result = await this.addItemsToCart(allItems, sections, cart);

      if (!result.ok) {
        this.handleErrorMessage(result.errorMsg);
        hasError = true;
        return;
      }

      const response = result.response;

      // ===== 渲染购物车弹窗 =====
      if (response && cart) {
        cart.setActiveElement(document.activeElement);

        if (typeof publish === 'function') {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'accessory-pickup',
              productVariantId: mainVariantId,
              cartData: response
            });
          } catch (e) {
            console.warn('[Accessory Pickup] publish cartUpdate warning:', e);
          }
        }

        const quickAddModal = this.mainProductForm.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => cart.renderContents(response));
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          cart.renderContents(response);
        }
      }

      if (!cart) {
        window.location = window.routes.cart_url;
      }
    } catch (e) {
      console.error('[Accessory Pickup] Fatal error:', e);
      this.handleErrorMessage(e.message || 'Unexpected error');
      hasError = true;
    } finally {
      // ---- UI: 恢复按钮状态 ----
      if (submitButton) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
      }
      if (loadingSpinner) loadingSpinner.classList.add('hidden');
      if (cart && cart.classList.contains('is-empty') && !hasError) {
        cart.classList.remove('is-empty');
      }
    }
  }

  handleErrorMessage(errorMessage = false) {
    const formWrapper = this.mainProductForm || this.form;
    const errorMessageWrapper = formWrapper.querySelector('.product-form__error-message-wrapper');
    if (!errorMessageWrapper) return;

    const errorMessageEl = errorMessageWrapper.querySelector('.product-form__error-message');
    if (!errorMessageEl) return;

    errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      errorMessageEl.textContent = errorMessage;
    }
  }
}

/* 初始化：在 DOM 加载完成后启动（防重复初始化） */
let _accessoryPickupInitialized = false;

if (!_accessoryPickupInitialized) {
  _accessoryPickupInitialized = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new AccessoryPickupCartHandler();
    });
  } else {
    new AccessoryPickupCartHandler();
  }
}
